/**
 * Čistý model sdíleného výběru destinace v adminu (`DestinationPicker.tsx`): bez Reactu, bez
 * `api` a bez `window`, aby šel testovat v projektu vitest `admin` (prostředí `node`).
 *
 * Tvar přebírá z webového výběru (`apps/web/src/lib/destinationPicker.ts`): strom „země → její
 * místa", kde zemí je každý řádek zemské úrovně (`isCountryLevel`, takže Bali je skupina mezi
 * zeměmi a Ubud pod ním, ne Bali pod Indonésií), a stav panelu jako vyjmenovaný union.
 * Odchyluje se v tom, co je dané účelem číselníku: nabízí VŠECHNY řádky (i nepublikované jako
 * „koncept"), bez kontinentů, bez stropu návrhů a bez minimální délky dotazu, řadí
 * `compareCzech` (připnutí se v adminu ignoruje, vize F1.8).
 *
 * Výběr z veřejných props normalizuje jednou `pickerSelection` (kontejner, pohled i test čtou
 * jen ji) a volba panelu je union `none | heading | destination` (`PickerOption`).
 *
 * Vstupní řádky pocházejí z keše React Query (`useAdminDestinationList`): model je NEMĚNÍ,
 * všechna řazení jdou nad kopiemi.
 */
import {
  compareCzech,
  isCountryLevel,
  normalizeSearchText,
  sortBySearchMatch,
  type AdminDestinationListItem,
} from "@ckgo2/shared";
import type { DestinationPickerLevel, DestinationPickerProps } from "./DestinationPicker.types";

/** Sloupce řádku, které výběr čte (podmnožina `AdminDestinationListItem`). */
export type PickerRow = Pick<
  AdminDestinationListItem,
  "id" | "name" | "parentId" | "showAsCountry" | "isPublished"
>;

/**
 * Jediná normalizace hledání ve výběru: kanonické `normalizeSearchText` (malá písmena, bez
 * diakritiky, sloučené mezery) a navíc `đ → d`, které NFD nerozloží (vietnamské „Đà Lạt").
 */
export function foldSearch(text: string): string {
  return normalizeSearchText(text.replace(/đ/gi, "d"));
}

export interface PickerEntry {
  row: PickerRow;
  parentName: string | null;
  level: "country" | "place";
  label: string;
  foldName: string;
  foldParent: string;
  foldBoth: string;
}

interface PickerGroup {
  entry: PickerEntry;
  children: PickerEntry[];
}

export interface PickerIndex {
  byId: ReadonlyMap<string, PickerEntry>;
  /** Řádky ve vstupním pořadí (pro hledání). */
  entries: readonly PickerEntry[];
  /** Skupiny zemské úrovně `compareCzech`, pod každou její přímá místa `compareCzech`. */
  groups: readonly PickerGroup[];
  /** Místa, jejichž rodič v seznamu chybí (nebo není zemské úrovně): konec stromu. */
  orphans: readonly PickerEntry[];
}

/**
 * Jedna řádka otevřeného panelu: volba „Bez destinace", nevybíratelný nadpis skupiny
 * (vyloučená země ve štítcích zájezdu), nebo destinace.
 */
export type PickerOption =
  | { kind: "none" }
  | { kind: "heading"; id: string; name: string }
  | {
      kind: "destination";
      id: string;
      name: string;
      level: "country" | "place";
      isDraft: boolean;
      /** „<místo> · <země>", když rodič existuje, jinak jméno. */
      label: string;
      /** Místo pod skupinou ve stromu (odsazené). */
      indent: boolean;
    };

export type DestinationOption = Extract<PickerOption, { kind: "destination" }>;

/** Volba, kterou jde vybrat (vše kromě nadpisu). */
export type SelectableOption = Exclude<PickerOption, { kind: "heading" }>;

export const isSelectable = (option: PickerOption): option is SelectableOption =>
  option.kind !== "heading";

/**
 * Jediná normalizace výběru z veřejných props: jedna hodnota je `[value]` (prázdná = `[]`),
 * více hodnot v uloženém pořadí. `allowNone` platí jen pro jednu hodnotu (výchozí `true`).
 */
export interface PickerSelection {
  multi: boolean;
  /** Vybraná ID v pořadí hodnoty (Set drží pořadí vložení). */
  selectedIds: ReadonlySet<string>;
  allowNone: boolean;
  excludeIds: readonly string[];
}

export function pickerSelection(props: DestinationPickerProps): PickerSelection {
  const excludeIds = props.excludeIds ?? [];
  if (props.mode === "multiple")
    return { multi: true, selectedIds: new Set(props.value), allowNone: false, excludeIds };
  return {
    multi: false,
    selectedIds: new Set(props.value ? [props.value] : []),
    allowNone: props.allowNone ?? true,
    excludeIds,
  };
}

/** Je volba vybraná? „Bez destinace" = prázdný výběr; nadpis nikdy. */
export function isOptionSelected(
  option: PickerOption,
  selection: Pick<PickerSelection, "selectedIds">,
): boolean {
  if (option.kind === "none") return selection.selectedIds.size === 0;
  return option.kind === "destination" && selection.selectedIds.has(option.id);
}

const byEntryName = (a: PickerEntry, b: PickerEntry) => compareCzech(a.row.name, b.row.name);

export function buildPickerIndex(rows: readonly PickerRow[]): PickerIndex {
  const rowById = new Map(rows.map((r) => [r.id, r]));
  const entries: PickerEntry[] = rows.map((row) => {
    const parentName = (row.parentId && rowById.get(row.parentId)?.name) || null;
    return {
      row,
      parentName,
      level: isCountryLevel(row) ? "country" : "place",
      label: parentName ? `${row.name} · ${parentName}` : row.name,
      foldName: foldSearch(row.name),
      foldParent: parentName ? foldSearch(parentName) : "",
      foldBoth: foldSearch(parentName ? `${row.name} ${parentName}` : row.name),
    };
  });
  const byId = new Map(entries.map((e) => [e.row.id, e]));

  const groups: PickerGroup[] = entries
    .filter((e) => e.level === "country")
    .sort(byEntryName)
    .map((entry) => ({ entry, children: [] }));
  const groupById = new Map(groups.map((g) => [g.entry.row.id, g]));
  const orphans: PickerEntry[] = [];
  for (const entry of entries) {
    if (entry.level === "country") continue;
    const group = entry.row.parentId ? groupById.get(entry.row.parentId) : undefined;
    if (group) group.children.push(entry);
    else orphans.push(entry);
  }
  for (const group of groups) group.children.sort(byEntryName);
  orphans.sort(byEntryName);

  return { byId, entries, groups, orphans };
}

function toOption(entry: PickerEntry, indent: boolean): PickerOption {
  return {
    kind: "destination",
    id: entry.row.id,
    name: entry.row.name,
    level: entry.level,
    isDraft: entry.row.isPublished === false,
    label: entry.label,
    indent,
  };
}

const NONE_OPTION: PickerOption = { kind: "none" };

export interface PickerOptionsInput {
  query: string;
  /** Výchozí `"all"`. */
  level?: DestinationPickerLevel;
  excludeIds?: readonly string[];
  /** Volba „Bez destinace" na začátku seznamu bez textu; výchozí `true`. */
  allowNone?: boolean;
}

/**
 * Volby panelu. Bez textu: strom (skupina, pod ní odsazená místa) nebo v `level: "country"`
 * plochý seznam zemské úrovně, s volbou „Bez destinace" na začátku (`allowNone`). S textem:
 * ploché volby podle stupně shody (`sortBySearchMatch` nad složenými řetězci), pak země před
 * místem celkově (F2.3), při shodě `compareCzech` podle jména a rodiče.
 *
 * `excludeIds`: vyloučený řádek se nenabízí; vyloučená skupina ve stromu zůstane jako
 * nevybíratelný nadpis, aby její místa zůstala seskupená (bez míst se vynechá celá).
 */
export function pickerOptions(index: PickerIndex, input: PickerOptionsInput): PickerOption[] {
  const { level = "all", allowNone = true } = input;
  const excluded = new Set(input.excludeIds ?? []);
  const query = foldSearch(input.query);

  if (query) {
    const pool = index.entries.filter(
      (e) => !excluded.has(e.row.id) && (level === "all" || e.level === "country"),
    );
    const matched = sortBySearchMatch([...pool], query, {
      name: (e) => e.foldName,
      other: (e) => [e.foldParent, e.foldBoth],
      isCountryLevel: (e) => e.level === "country",
      tieBreak: (a, b) => byEntryName(a, b) || compareCzech(a.parentName, b.parentName),
    });
    return [
      ...matched.filter((e) => e.level === "country"),
      ...matched.filter((e) => e.level !== "country"),
    ].map((e) => toOption(e, false));
  }

  const options: PickerOption[] = allowNone ? [NONE_OPTION] : [];
  if (level === "country") {
    for (const group of index.groups) {
      if (!excluded.has(group.entry.row.id)) options.push(toOption(group.entry, false));
    }
    return options;
  }
  for (const group of index.groups) {
    const children = group.children.filter((c) => !excluded.has(c.row.id));
    const groupExcluded = excluded.has(group.entry.row.id);
    if (groupExcluded && children.length === 0) continue;
    options.push(
      groupExcluded
        ? { kind: "heading", id: group.entry.row.id, name: group.entry.row.name }
        : toOption(group.entry, false),
    );
    for (const child of children) options.push(toOption(child, true));
  }
  for (const orphan of index.orphans) {
    if (!excluded.has(orphan.row.id)) options.push(toOption(orphan, false));
  }
  return options;
}

/**
 * Popisek uložené hodnoty pro zavřený výběr a štítky; hledá v celém indexu bez ohledu na
 * `level` (průvodce s uloženým místem ho ukáže s rodičem). `null` = prázdná nebo neznámá
 * hodnota.
 */
export function pickerLabel(
  index: PickerIndex,
  id: string | null | undefined,
): { text: string; isDraft: boolean } | null {
  if (!id) return null;
  const entry = index.byId.get(id);
  return entry ? { text: entry.label, isDraft: entry.row.isPublished === false } : null;
}

export type PickerPanel =
  | { kind: "loading" }
  | { kind: "error"; retry: () => void }
  | { kind: "empty" }
  | { kind: "options"; options: PickerOption[] };

/**
 * Stav panelu. Pořadí větví je podstatné: TanStack Query v5 drží po chybě `status: "error"`
 * i během `refetch()`, takže „chyba" se smí ukázat jen, když dotaz neběží; jinak by po
 * „Zkusit znovu" svítila dál místo načítání.
 */
export function pickerPanel(input: {
  hasData: boolean;
  isError: boolean;
  isFetching: boolean;
  options: PickerOption[];
  onRetry: () => void;
}): PickerPanel {
  if (!input.hasData) {
    if (input.isError && !input.isFetching) return { kind: "error", retry: input.onRetry };
    return { kind: "loading" };
  }
  if (input.options.length === 0) return { kind: "empty" };
  return { kind: "options", options: input.options };
}

/** Posun zvýraznění o ±1 přes nevybíratelné nadpisy; na krajích stojí (bez přetočení). */
export function moveHighlight(
  current: number,
  delta: 1 | -1,
  options: readonly PickerOption[],
): number {
  for (let i = current + delta; i >= 0 && i < options.length; i += delta) {
    const option = options[i];
    if (option && isSelectable(option)) return i;
  }
  return current;
}

/**
 * Výchozí zvýraznění: u jedné hodnoty vybraná volba (prázdná hodnota = „Bez destinace", je-li
 * v seznamu), jinak (u více hodnot a bez `selection`, tj. při psaní) první vybíratelná;
 * `-1` = žádná.
 */
export function firstHighlight(
  options: readonly PickerOption[],
  selection?: Pick<PickerSelection, "multi" | "selectedIds">,
): number {
  if (selection && !selection.multi) {
    const selected = options.findIndex((o) => isOptionSelected(o, selection));
    if (selected !== -1) return selected;
  }
  return options.findIndex(isSelectable);
}

/** Přepne ID v hodnotě více destinací: přidá na konec, nebo odebere; pořadí ostatních drží. */
export function toggleId(values: readonly string[] | null | undefined, id: string): string[] {
  const list = values ?? [];
  return list.includes(id) ? list.filter((v) => v !== id) : [...list, id];
}
